// Screen areas that belong to the app, not to windows.
//
// On the desktop app, a drag rail spans the top of the screen above every
// window. Every press inside it moves the app window, and the app's own
// new-window, minimize and close buttons sit at its right end. A window whose
// title bar lies under the rail cannot be dragged, minimized or closed, so
// the person has no way to get it back.
//
// Placement treats the rail as space no window may take: new, restored and
// relocated windows go below it, and a saturated workspace covers other
// windows before it covers the rail. Geometry that reaches the store without
// placement (a layout saved in another session, a smaller viewport, a window
// that resizes itself) is fitted with fitWindowsIntoWorkspace. The rail component reads its height from
// here, so placement and paint cannot drift apart.
//
// Other chrome is not reserved. The power button in the bottom-right corner
// sits under the windows by design (z-0 while closed), and a window over it
// only hides it until the person moves that window; it never traps a window.

import type { LayoutPoint, LayoutRect, LayoutSize } from "@/src/state/appStore/layoutGeometry";

/** Height of the desktop app's drag rail across the top of the screen. */
export const DESKTOP_DRAG_RAIL_HEIGHT = 48;

export type WorkspaceChromeOptions = {
  /** Whether the desktop app's drag rail is on screen (Electron only). */
  desktopRail: boolean;
};

/**
 * The rects of `screen` that app chrome draws over, without any margin.
 * The mobile layout stacks windows in a scrolling column under its own
 * chrome, so callers pass no zones there.
 */
export function workspaceChromeZones(
  screen: LayoutSize,
  options: WorkspaceChromeOptions,
): LayoutRect[] {
  if (!options.desktopRail) return [];
  return [{ x: 0, y: 0, width: screen.width, height: DESKTOP_DRAG_RAIL_HEIGHT }];
}

/**
 * The part of `screen` a window may cover: `margin` inside every edge and,
 * on the desktop app, below the drag rail.
 */
export function workspaceBounds(
  screen: LayoutSize,
  options: WorkspaceChromeOptions,
  margin: number,
): LayoutRect {
  const top = (options.desktopRail ? DESKTOP_DRAG_RAIL_HEIGHT : 0) + margin;
  return {
    x: margin,
    y: top,
    width: Math.max(1, screen.width - margin * 2),
    height: Math.max(1, screen.height - top - margin),
  };
}

/**
 * The largest size a window can have and still sit clear of the drag rail
 * with `margin` on every side. A window taller than that would always put
 * its title bar under the rail, so placement opens it at this size instead.
 */
export function maxWindowSize(
  screen: LayoutSize,
  options: WorkspaceChromeOptions,
  margin: number,
): LayoutSize {
  const bounds = workspaceBounds(screen, options, margin);
  return { width: bounds.width, height: bounds.height };
}

/**
 * `windows` moved, and on the desktop app shrunk, so they lie inside the
 * workspace bounds. The windows move as one block by the same offset, so a
 * docked group keeps its shape; pass a single window on its own.
 *
 * With the drag rail, each window is first capped at `maxWindowSize`, since a
 * taller one would put its title bar under the rail wherever it sat. Without
 * the rail a window keeps its size, as placement leaves it. When the block is
 * still larger than the workspace, its top-left edge wins, so the title bars
 * stay reachable and only the far edge runs off screen.
 *
 * Returns the input array itself when nothing has to change.
 */
export function fitWindowsIntoWorkspace<T extends LayoutRect>(
  windows: readonly T[],
  screen: LayoutSize,
  options: WorkspaceChromeOptions,
  margin: number,
): readonly T[] {
  if (windows.length === 0) return windows;
  const bounds = workspaceBounds(screen, options, margin);
  const sized = windows.map((window) => (
    options.desktopRail
      ? {
          ...window,
          width: Math.min(window.width, bounds.width),
          height: Math.min(window.height, bounds.height),
        }
      : window
  ));
  const left = Math.min(...sized.map((window) => window.x));
  const top = Math.min(...sized.map((window) => window.y));
  const right = Math.max(...sized.map((window) => window.x + window.width));
  const bottom = Math.max(...sized.map((window) => window.y + window.height));
  const fitAxis = (start: number, extent: number, min: number, room: number) =>
    Math.max(min, Math.min(min + room - extent, start)) - start;
  const dx = fitAxis(left, right - left, bounds.x, bounds.width);
  const dy = fitAxis(top, bottom - top, bounds.y, bounds.height);
  const fitted = sized.map((window) => (
    dx === 0 && dy === 0 ? window : { ...window, x: window.x + dx, y: window.y + dy }
  ));
  const changed = fitted.some((window, index) => (
    window.x !== windows[index].x
    || window.y !== windows[index].y
    || window.width !== windows[index].width
    || window.height !== windows[index].height
  ));
  return changed ? fitted : windows;
}

/**
 * Spots just clear of each zone that a `size` window at `position` would
 * cover with its `margin`: flush below, above, left and right of the zone,
 * keeping the other coordinate. The nearest-spot search steps in coarse
 * rings, so it tries these first to move a window only as far as the chrome
 * needs (a window saved under the rail lands right below it).
 */
export function chromeClearanceSeeds(
  position: LayoutPoint,
  size: LayoutSize,
  zones: LayoutRect[],
  margin: number,
): LayoutPoint[] {
  const seeds: LayoutPoint[] = [];
  for (const zone of zones) {
    const covers = position.x - margin < zone.x + zone.width
      && position.x + size.width + margin > zone.x
      && position.y - margin < zone.y + zone.height
      && position.y + size.height + margin > zone.y;
    if (!covers) continue;
    seeds.push(
      { x: position.x, y: zone.y + zone.height + margin },
      { x: position.x, y: zone.y - size.height - margin },
      { x: zone.x + zone.width + margin, y: position.y },
      { x: zone.x - size.width - margin, y: position.y },
    );
  }
  return seeds;
}
